import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Query } from '~/lazy_apollo/client/react/components';
import { Helmet } from 'react-helmet';

import { PopmenuConfigContext } from '~/utils/withPopmenuConfig';
import { getAppStaticProps } from '~/utils/appStaticProps';
import SelfSignUpContainer from '~/admin/sign-up/SelfSignUp/SelfSignUpContainer';
import AppointmentLinkPage from '~/admin/sign-up/SelfSignUp/AppointmentLinkPage';
import ResellerSignUpContainer from './sign-up/ResellerSignUpContainer/ResellerSignUpContainer.imports-loadable';
import { UserThemeProvider } from './UserThemeProvider';
import { CurrentSessionContextProvider } from '../shared/CurrentSessionProvider';
import adminSessionQuery from '../libs/gql/queries/users/adminSessionQuery.gql';

import AdminParentContainer from './AdminContainer/AdminParentContainer.imports-loadable';
import GenericContainer from './GenericContainer/GenericContainer';
import LazyIntlProvider from './LazyIntlProvider';
import MessagePreviewContainer from './messages/MessagePreviewContainer';
import OAuthRestaurantSelection from '../oauth/OAuthRestaurantSelection';
import PopmenuLoading from './shared/PopmenuLoading';
import SignUpContainer from './sign-up/SignUpContainer';
import ModularSignUpContainer from './sign-up/ModularSignUp/components/ModularSignUpContainer/ModularSignUpContainer.imports-loadable';
import { GetWaitlistContainer } from './sign-up/GetWaitlistContainer';
import StatusPrompt from './shared/StatusPrompt';
import VersionCheck from './shared/version_check';
import { AdminPageChangeHandler } from './AdminPageChangeHandler';
import ExistingAccountNotice from './sign-up/SelfSignUp/ExistingAccountNotice';
import { SelfSignUpPlanRoute } from './sign-up/SelfSignUp/SelfSignUpPlanRoute';

const checkMfaVerified = (session) => {
  if (session.isMfaRequired) {
    return session.isMfaVerified;
  }
  if (session.isAuthyRequired && !session.isMfaRequired) {
    return session.isAuthyVerified;
  }
  return true;
};

const AdminSwitch = () => (
  <Query query={adminSessionQuery} pollInterval={180000}>
    {({ data, refetch }) => {
      if (!data || !data.currentSession) {
        return <PopmenuLoading />;
      }
      console.log(`[POPMENU] Popmenu Version ${data.popmenuConfig.popmenuVersion}, Session ${data.currentSession.id}, User ${data.currentSession.user ? data.currentSession.user.id : '(none)'}`);
      const session = data.currentSession;
      const {
        facebookOauthDisabled,
        afsSsupMenuEmbedded,
        isAteamModularSignUpFlowEnabled,
        isAteamGetWaitlistTrialEnabled,
      } = data.popmenuConfig;
      const userMembers = session.user?.members || [];
      const hasExistingRestaurant = userMembers.length > 0;

      // Routes that should be always be displayed outside the main AdminContainer
      const genericRoutes = [
        '/accessibility',
        '/authenticate',
        '/client-terms',
        '/dmca-policy',
        '/do-not-sell-my-personal-info',
        '/invites/:token',
        '/link',
        '/ncr/sign-up',
        '/reseller/sign-up',
        '/privacy',
        '/profile',
        '/reset/:token',
        '/setup',
        '/setup-link',
        '/sign-up',
        '/subscriptions/:unsubscribeToken',
        '/unsubscribed/:unsubscribeToken',
        '/terms',
      ];

      // Fallback to generic container for ALL routes if user is unauthenticated
      const isMfaVerified = checkMfaVerified(session);
      const isAuthenticated = session.user ? isMfaVerified : false;

      let defaultContainer;
      if (isAuthenticated) {
        defaultContainer = <AdminParentContainer />;
      } else {
        defaultContainer = <GenericContainer refetch={refetch} facebookOauthDisabled={facebookOauthDisabled} />;
      }

      const searchParams = new URLSearchParams(document.location.search);
      let returnTo = searchParams.get('return_to');

      if (session.user && isMfaVerified && returnTo) {
        returnTo = atob(unescape(returnTo));
        window.location.assign(returnTo);
      }

      if (session.user && !isMfaVerified && returnTo && !window.location.pathname.includes('authenticate')) {
        window.location.assign(`/authenticate/?return_to=${returnTo}`);
      }

      return (
        <LazyIntlProvider adminLocale={session.user?.adminLocale}>
          <UserThemeProvider>
            <PopmenuConfigContext.Provider value={data.popmenuConfig}>
              <CurrentSessionContextProvider value={session}>
                <React.Fragment>
                  <Helmet
                    defaultTitle={getAppStaticProps().platformBrandName}
                    titleTemplate={`%s - ${getAppStaticProps().platformBrandName}`}
                  />
                  <AdminPageChangeHandler branchIoKey={data.popmenuConfig.branchIoKey} />
                  <Switch>
                    <Route path="/ncr/sign-up">
                      <SignUpContainer />
                    </Route>
                    <Route path="/reseller/sign-up">
                      <ResellerSignUpContainer />
                    </Route>
                    <Route path="/sign-up/appointment-link">
                      <AppointmentLinkPage />
                    </Route>
                    <Route path="/get-waitlist">
                      {isAteamGetWaitlistTrialEnabled ? <GetWaitlistContainer /> : <Redirect to="/" /> }
                    </Route>
                    {isAteamModularSignUpFlowEnabled && (
                      <Route path="/sign-up-v2/:step?">
                        <ModularSignUpContainer />
                      </Route>
                    )}
                    <Route path="/sign-up/existing-account">
                      {(isAuthenticated && hasExistingRestaurant) ? <ExistingAccountNotice /> : <Redirect to={afsSsupMenuEmbedded ? '/sign-up/starter' : '/sign-up'} />}
                    </Route>
                    {afsSsupMenuEmbedded && (
                      <Route path="/sign-up/:plan">
                        <SelfSignUpPlanRoute
                          isAuthenticated={isAuthenticated}
                          hasExistingRestaurant={hasExistingRestaurant}
                          afsSsupMenuEmbedded={afsSsupMenuEmbedded}
                        />
                      </Route>
                    )}
                    <Route path="/sign-up">
                      {(isAuthenticated && hasExistingRestaurant) ? <Redirect to="/sign-up/existing-account" /> : <SelfSignUpContainer />}
                    </Route>
                    <Route path="/self-sign-up">
                      {(isAuthenticated && hasExistingRestaurant) ? <Redirect to="/sign-up/existing-account" /> : <SignUpContainer />}
                    </Route>
                    <Route path="/messages/:token">
                      <MessagePreviewContainer />
                    </Route>
                    {genericRoutes.map(route => (
                      <Route key={route} path={route}>
                        <GenericContainer refetch={refetch} facebookOauthDisabled={facebookOauthDisabled} />
                      </Route>
                    ))}
                    <Route path="/oauth/:accountType">
                      <OAuthRestaurantSelection isAuthenticated={isAuthenticated} />
                    </Route>
                    <Route path="/" exact>
                      <GenericContainer refetch={refetch} facebookOauthDisabled={facebookOauthDisabled} />
                    </Route>
                    <Route path="/:restaurantSlug">
                      {defaultContainer}
                    </Route>
                    <Route path="/">
                      {defaultContainer}
                    </Route>
                  </Switch>
                  <StatusPrompt />
                  <VersionCheck />
                </React.Fragment>
              </CurrentSessionContextProvider>
            </PopmenuConfigContext.Provider>
          </UserThemeProvider>
        </LazyIntlProvider>
      );
    }}
  </Query>
);

export default AdminSwitch;
